import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import "./footer.css";
import SocialLinks from "../socialLinks/socialLinks";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import logo from "../../../assets/images/Logo.svg";
import Applelogo from "../../../assets/images/Apple.webp";
import googlelogo from "../../../assets/images/Google.webp";
import { useSelector } from "react-redux";
import { selectHeaderMenu } from "../../../redux/features/toolsSlice";
import { LIFE_LANDING_BASE_URL } from "../../../api/baseURL";

import overview from "../overviewItem";
import FooterData from "./footerData";
import FooterdataArabic from "./footerarabic";
import {
  call_us,
  speak_to_advisor,
  general_insurance_slug,
  for_businesses_slug,
  claims_slug,
  support_slug,
  resources_slug,
  more_slug,
} from "../../../utils/slugs";

import ReactGA from "react-ga4";
import { selectPlanHeader } from "../../../redux/features/plansSlice";

const Footer = () => {
  const translate = useLanguageTranslate();
  const [render, setRender] = useState(null);
  const [menuGroups, setMenuGroups] = useState([]);
  const [isSpeakToAdvisor, setIsSpeakToAdvisor] = useState(false);

  const toggleModal = () => setIsSpeakToAdvisor(!isSpeakToAdvisor);

  const headerdata = useSelector(selectPlanHeader);
  const headermenu = useSelector(selectHeaderMenu);

  useEffect(() => {
    if (headerdata && headermenu) {
      const formattedData = headerdata.map((category) => {
        const slug = category?.slug;
        const submenu = category?.plans?.map((plan) => ({
          name: {
            en: plan.content.subtitle_en,
            ar: plan.content.subtitle_ar,
          },
          link: `/product/${slug}/${plan.slug}`,
          isInternal: true,
        }));
        const overviewItem = overview(slug);
        return {
          category: {
            name: {
              en: category.content.subtitle_en,
              ar: category.content.subtitle_ar,
            },
          },
          items: [overviewItem, ...submenu],
        };
      });
      setRender(formattedData);
      setMenuGroups(headermenu);
    }
  }, [headerdata, headermenu]);

  const trackGAEvent = (category, action, label) => {
    ReactGA.event({ category, label, action });
  };

  const footerSections = useMemo(() => {
    const findParent = (slug) => menuGroups?.find((g) => g.slug === slug);
    const getItems = (slug) => {
      const parent = findParent(slug);
      return (
        parent?.menu_groups?.flatMap((mg) => mg.menu_group?.menu || []) || []
      );
    };
    const getHeading = (slug) => {
      const parent = findParent(slug);
      const firstGroup = parent?.menu_groups?.[0]?.menu_group;
      return {
        en: firstGroup?.category_en || parent?.label?.title_en || "",
        ar: firstGroup?.category_ar || parent?.label?.title_ar || "",
      };
    };

    const generalItems = getItems(general_insurance_slug);
    const forBizItems = getItems(for_businesses_slug);

    return {
      insurance: {
        heading: { en: "Insurance Policies", ar: "وثائق التأمين" },
        items: [...generalItems, ...forBizItems],
      },
      claims: {
        heading: getHeading(claims_slug),
        items: getItems(claims_slug),
      },
      tools: {
        heading: getHeading(resources_slug),
        items: getItems(resources_slug),
      },
      more: {
        heading: getHeading(more_slug),
        items: getItems(more_slug),
      },
      support: {
        heading: getHeading(support_slug),
        items: getItems(support_slug),
      },
    };
  }, [menuGroups]);

  const renderMenuItem = (menuItem, index) => {
    if (menuItem?.is_hide_in_footer === true) return null;

    const isInternal = menuItem?.is_internal;
    const isSpeakAdvisor = menuItem?.custom_action === speak_to_advisor;
    const isCallUs = menuItem?.custom_action === call_us;

    return (
      <li key={menuItem?.id || `item-${index}`}>
        {isInternal ? (
          isSpeakAdvisor ? (
            <Link
              className="flex gap-2 items-center"
              onClick={() => {
                trackGAEvent("Internal Links", "Click", menuItem.label_en);
                toggleModal();
              }}
            >
              {translate(menuItem?.label_en, menuItem?.label_ar)}
            </Link>
          ) : (
            <Link
              to={menuItem?.link}
              onClick={() =>
                trackGAEvent("Internal Links", "Click", menuItem.label_en)
              }
            >
              {translate(menuItem?.label_en, menuItem?.label_ar)}
            </Link>
          )
        ) : (
          <a
            href={isCallUs ? `tel:${menuItem?.link}` : menuItem?.link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackGAEvent("External Links", "Click", menuItem.label_en)
            }
          >
            {translate(menuItem?.label_en, menuItem?.label_ar)}
          </a>
        )}
      </li>
    );
  };

  const renderMenuSection = (
    heading,
    items,
    className,
    extraClassName = "",
  ) => {
    if (!heading || !items || items.length === 0) return null;
    return (
      <div className={`footer-list-box columns ${className} ${extraClassName}`}>
        <h4>{translate(heading.en, heading.ar)}</h4>
        <ul>{items.map((item, idx) => renderMenuItem(item, idx))}</ul>
      </div>
    );
  };

  const renderPlanSection = (data, className, extraClassName = "") => {
    if (!data) return null;
    return (
      <div className={`footer-list-box columns ${className} ${extraClassName}`}>
        <h4>{translate(data.category.name.en, data.category.name.ar)}</h4>
        <ul>
          {data.items?.map((item, idx) => (
            <li key={idx}>
              {item?.isInternal ? (
                <Link
                  to={internalLinkClickHandler(item.link)}
                  onClick={() =>
                    trackGAEvent("Internal Links", "Click", item.name.en)
                  }
                >
                  {translate(item.name.en, item.name.ar)}
                </Link>
              ) : (
                <a
                  href={internalLinkClickHandler(item.link)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackGAEvent("External Links", "Click", item.name.en)
                  }
                >
                  {translate(item.name.en, item.name.ar)}
                </a>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  if (!headermenu || headermenu.length === 0) {
    return null;
  }
  const internalLinkClickHandler = (link) => {
    let url = `${LIFE_LANDING_BASE_URL}${link}`;
    return url;
  };
  return (
    <footer className="flex footer-section">
      <div className="container mx-auto footer-flex">
        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 xl:grid-cols-1">
          <div className="w-/5 md:w-5/5 lg:w-5/5 xl:w-5/5">
            <div className="logo-footer-box">
              <img src={logo} alt="logo" />
            </div>
            <div className="apple-logo-box flex">
              <a
                href="https://apps.apple.com/bh/app/solidarity/id1585138622"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackGAEvent("Download App", "Click", "Apple Store")
                }
              >
                <img
                  className="applelogoimg"
                  src={Applelogo}
                  alt="Apple logo"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=bh.solidarity.app&hl=en&gl=US"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackGAEvent("Download App", "Click", "Google Play")
                }
              >
                <img
                  className="applelogoimg"
                  src={googlelogo}
                  alt="Google logo"
                />
              </a>
            </div>
            <SocialLinks />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4">
          <div className="columns">
            {renderMenuSection(
              footerSections.insurance.heading,
              footerSections.insurance.items,
              "Insurance-Policies",
            )}
            {renderMenuSection(
              footerSections.claims.heading,
              footerSections.claims.items,
              "Claims w-full",
              "hidden md:block",
            )}
          </div>

          <div className="columns">
            {renderPlanSection(render?.[0], "Savings-Protection-Plans")}
            {renderPlanSection(
              render?.[1],
              "Protection-Plans w-full",
              "hidden md:block",
            )}
          </div>

          <div className="column block md:hidden">
            {renderPlanSection(render?.[1], "Protection-Plans")}
          </div>

          <div className="columns">
            {render &&
              render.length > 2 &&
              renderPlanSection(render[2], "Buy-Online-Plans")}
            {renderMenuSection(
              footerSections.tools.heading,
              footerSections.tools.items,
              "Resources",
              "hidden md:block",
            )}
            {renderMenuSection(
              footerSections.more.heading,
              footerSections.more.items,
              "More",
              "hidden md:block",
            )}
          </div>

          <div className="columns block md:hidden">
            {renderMenuSection(
              footerSections.claims.heading,
              footerSections.claims.items,
              "Claims w-full",
            )}
          </div>

          <div className="columns block md:hidden">
            {renderMenuSection(
              footerSections.tools.heading,
              footerSections.tools.items,
              "Resources",
            )}
          </div>

          <div className="columns">
            {renderMenuSection(
              footerSections.support.heading,
              footerSections.support.items,
              "Support",
            )}
          </div>

          <div className="columns block md:hidden">
            {renderMenuSection(
              footerSections.more.heading,
              footerSections.more.items,
              "More",
            )}
          </div>
        </div>
      </div>

      {translate(<FooterData />, <FooterdataArabic />)}
    </footer>
  );
};

export default Footer;
